import { GridColDef, GridCellParams } from '@mui/x-data-grid'
import { t } from 'i18next'
import { NavLink, useNavigate } from 'react-router-dom'
import DeleteButton from '../components/common/DeleteButton'
import LaunchIcon from '@mui/icons-material/Launch'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import TagPriority from '../components/common/TagPriority'
import { useSelector } from 'react-redux'
import { UserRole } from '../types/keycloak'
import { hasRole } from '../utils/checkRole'
import { formatTitle } from '../utils/formatTitle'
import { formatDecimal } from '../utils/formatNumbers'
import { numericColumn } from '../utils/dataGridStyles'
import { systemLabel } from '../utils/systemLabel'

const useColumnsIncident = (
  handleDeleteClick?: (incidentId: string) => void,
  disableActions?: boolean
): GridColDef[] => {
  const navigate = useNavigate()
  const roles = useSelector((state: any) => state.auth.roles)
  disableActions = disableActions ?? false
  return [
    {
      field: 'IncidentName',
      headerName: t('tables.incidentTable.name'),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <NavLink
          className="line-clamp-2 overflow-hidden text-ellipsis"
          to={`/incident-detail/${params.id}`}
        >
          <strong style={{ color: '#132743' }}>
            {formatTitle(params.value as string)}
          </strong>
        </NavLink>
      ),
    },
    {
      field: 'Description',
      headerName: t('tables.incidentTable.description'),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.row.Description}
        </span>
      ),
    },
    {
      field: 'MissionName',
      headerName: t('tables.incidentTable.mission_name'),
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.row.MissionName}
        </span>
      ),
    },
    {
      field: 'Perimeter',
      headerName: t('tables.incidentTable.perimeter'),
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.row.Perimeter
            ? systemLabel(t, 'incidentPerimeter', params.row.Perimeter)
            : ''}
        </span>
      ),
    },
    {
      field: 'Criticality',
      headerName: t('tables.incidentTable.criticality'),
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          <TagPriority priority={params.row.Criticality} />
        </span>
      ),
    },
    {
      field: 'Type',
      headerName: t('tables.incidentTable.type'),
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.row.Type}
        </span>
      ),
    },
    {
      field: 'Vulnerability',
      headerName: t('tables.incidentTable.vulnerability'),
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.row.Vulnerability}
        </span>
      ),
    },
    {
      field: 'SLA',
      headerName: t('tables.incidentTable.sla'),
      flex: 1,
      ...numericColumn(100),
      renderCell: (params: GridCellParams) => (
        <span className="incyia-num">
          {formatDecimal(params.row.SLA, { decimals: 0 })}
        </span>
      ),
    },
    ...(disableActions === false
      ? [
          {
            field: 'Action',
            headerName: t('tables.incidentTable.action'),
            minWidth: 100,
            flex: 0.5,
            renderCell: (params: GridCellParams) => (
              <>
                <LaunchIcon
                  className="iconlaunch"
                  onClick={() => navigate(`/incident-detail/${params.id}`)}
                />
                {hasRole(roles, UserRole.RSSI) ||
                hasRole(roles, UserRole.SSI_TEAM) ||
                hasRole(roles, UserRole.ADMIN) ? (
                  <DriveFileRenameOutlineOutlinedIcon
                    className="iconEdit"
                    onClick={() => navigate(`/incident/${params.id}`)}
                  />
                ) : (
                  ''
                )}
                {hasRole(roles, UserRole.RSSI) ||
                hasRole(roles, UserRole.SSI_TEAM) ||
                hasRole(roles, UserRole.ADMIN) ? (
                  <DeleteButton
                    onDelete={() =>
                      handleDeleteClick &&
                      handleDeleteClick(params.id as string)
                    }
                    item={{
                      entityType: t('incidents', {
                        defaultValue: 'Incident',
                      }),
                      name: params.row.IncidentName
                        ? formatTitle(String(params.row.IncidentName))
                        : undefined,
                      details: [
                        {
                          label: t('deleteConfirm.fields.status'),
                          value: params.row.Status || '—',
                        },
                      ],
                    }}
                  />
                ) : (
                  ''
                )}
              </>
            ),
          },
        ]
      : []),
  ]
}

export default useColumnsIncident
